import React, { useState, useMemo } from 'react';
import './RentedAsics.css';
import { useLanguage } from '../../context/LanguageContext';
import { useUser } from '../../context/UserContext';
import { getTranslation } from '../../i18n/translations';
import '../../components/Button/Button.css';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const RentedAsics = () => {
  const { language } = useLanguage();
  const { user } = useUser();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15; // 15 data points per page
  const totalPages = 5;

  // Mock data
  const totalReward = {
    btc: '0.00000000',
    usd: '0',
    monthlyBtc: '0.00000000'
  };

  const asicsRented = 0;
  const totalMiningPower = 0; // TH

  // Empty array for now - table is empty in HTML
  const rentedAsics = [];

  const handleAddMore = () => {
    window.history.pushState({ page: 'rent-asics' }, '', '/mining-store/rent-asics');
    window.location.reload();
  };

  // Generate chart data based on current page
  const chartData = useMemo(() => {
    // Generate dates starting from Dec 29 (based on HTML structure)
    const startDate = new Date(2024, 11, 29); // Month is 0-indexed, so 11 = December
    const data = [];
    
    const baseValues = [
      0.00023558, 0.00024500, 0.00025600, 0.00024500, 0.00037125,
      0.00036000, 0.00024500, 0.00025600, 0.00030000, 0.00034500,
      0.00038000, 0.00029000, 0.00022800, 0.00034500, 0.00028000
    ];
    
    for (let i = 0; i < itemsPerPage; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + (currentPage - 1) * itemsPerPage + i);
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const dateStr = `${day}.${month}`;
      
      // Use base values or generate random values around 0.0003
      const value = baseValues[i] || (0.0002 + Math.random() * 0.0005);
      
      data.push({
        date: dateStr,
        value: value
      });
    }
    
    return data;
  }, [currentPage, itemsPerPage]);

  // Chart configuration
  const chartOptions = useMemo(() => ({
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      spacing: [10, 10, 10, 10],
      height: 400
    },
    title: {
      text: ''
    },
    xAxis: {
      categories: chartData.map(item => item.date),
      labels: {
        rotation: -90,
        align: 'right',
        style: {
          fontSize: '0.8em',
          color: '#333333'
        }
      },
      lineColor: '#333333',
      lineWidth: 1,
      tickColor: '#333333'
    },
    yAxis: {
      title: {
        text: ''
      },
      labels: {
        format: '{value:.7f}',
        style: {
          fontSize: '0.8em',
          color: '#333333'
        }
      },
      gridLineColor: '#e6e6e6',
      gridLineWidth: 1
    },
    plotOptions: {
      column: {
        borderRadius: 3,
        borderWidth: 1,
        borderColor: '#ffffff',
        color: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, '#96E6C2'],
            [1, '#E0F7ED']
          ]
        }
      }
    },
    series: [{
      name: 'Daily Revenue',
      data: chartData.map(item => item.value),
      showInLegend: false
    }],
    tooltip: {
      enabled: true,
      formatter: function() {
        const index = this.x;
        const dataPoint = chartData[index];
        return `<b>${dataPoint.date}</b><br/>Value: ${this.y.toFixed(8)}<br/>FPPS: ${(this.y * 0.142).toFixed(8)}<br/>Rate: 0.00`;
      },
      backgroundColor: '#ffffff',
      borderColor: '#96E6C2',
      borderRadius: 3,
      style: {
        fontSize: '0.8em',
        color: '#333333'
      }
    },
    credits: {
      enabled: false
    }
  }), [chartData]);

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  return (
    <div className="rent-mining-farm">
      <div className="rent-mining-farm__header">
        <svg
          width="24"
          height="25"
          viewBox="0 0 24 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="rent-mining-farm__img"
        >
          <g clipPath="url(#clip0_6330_32693)">
            <rect x="0.43125" y="1.025" width="22.95" height="22.95" rx="4.275" stroke="#1570EF" strokeWidth="1.05"></rect>
            <path d="M7.7062 7H16.2937C16.3928 7.00017 16.4897 7.0298 16.572 7.08515C16.6542 7.14049 16.7182 7.21904 16.7557 7.31081C16.7932 7.40259 16.8026 7.50344 16.7826 7.60056C16.7627 7.69768 16.7143 7.78668 16.6437 7.85625L11.9999 12.5L7.3562 7.85625C7.28555 7.78668 7.23719 7.69768 7.21725 7.60056C7.19731 7.50344 7.20669 7.40259 7.2442 7.31081C7.28172 7.21904 7.34567 7.14049 7.42793 7.08515C7.51019 7.0298 7.60705 7.00017 7.7062 7V7Z" stroke="#1570EF" strokeLinecap="round" strokeLinejoin="round"></path>
            <path d="M7.7062 18H16.2937C16.3928 17.9998 16.4897 17.9702 16.572 17.9149C16.6542 17.8595 16.7182 17.781 16.7557 17.6892C16.7932 17.5974 16.8026 17.4966 16.7826 17.3994C16.7627 17.3023 16.7143 17.2133 16.6437 17.1438L11.9999 12.5L7.3562 17.1438C7.28555 17.2133 7.23719 17.3023 7.21725 17.3994C7.19731 17.4966 7.20669 17.5974 7.2442 17.6892C7.28172 17.781 7.34567 17.8595 7.42793 17.9149C7.51019 17.9702 7.60705 17.9998 7.7062 18Z" stroke="#1570EF" strokeLinecap="round" strokeLinejoin="round"></path>
            <path d="M8.5 9H15.5" stroke="#1570EF" strokeLinecap="round" strokeLinejoin="round"></path>
          </g>
          <defs>
            <clipPath id="clip0_6330_32693">
              <rect width="24" height="24" fill="white" transform="translate(0 0.5)"></rect>
            </clipPath>
          </defs>
        </svg>
        <h1 className="rent-mining-farm__title">{getTranslation('rentedAsics', language)}</h1>
      </div>

      <div className="rent-mining-farm__block">
        <div className="rent-mining-farm__card-block">
          <h2 className="rent-mf__title-block">{getTranslation('myRentedMiningFarm', language)}</h2>
          <div className="rent-mining-farm__card">
            {/* Total Mining Reward */}
            <div className="rent-mf rent-mf_second">
              <p className="rent-mf__title">{getTranslation('totalMiningReward', language)}</p>
              <div>
                <div className="rent-mf__crypto">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    className="overview__image"
                    alt="crypto-logo"
                  >
                    <rect
                      x="1.07676"
                      y="0.525"
                      width="22.95"
                      height="22.95"
                      rx="5.475"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                    />
                    <path
                      d="M14.9702 7.00768L15.4347 5.2647L15.8701 5.38115L15.3963 7.13586L15.2599 7.6412L15.7698 7.7541L15.77 7.75414L15.7703 7.75423L15.7706 7.75429L15.7709 7.75436L15.7719 7.75458L15.7852 7.75779C15.798 7.76096 15.8186 7.76621 15.846 7.77373C15.9008 7.7888 15.9823 7.81286 16.083 7.84743C16.2852 7.91691 16.5594 8.02703 16.8469 8.18861C17.4304 8.51652 18.0105 9.02189 18.2285 9.77565C18.4428 10.5164 18.2135 11.0531 17.8789 11.4282C17.7058 11.6223 17.5033 11.7724 17.3185 11.8746C17.2266 11.9254 17.1433 11.962 17.0754 11.9858C17.0416 11.9976 17.0136 12.0055 16.992 12.0105C16.9812 12.013 16.9728 12.0146 16.9666 12.0156L16.9595 12.0166L16.9576 12.0168C16.9573 12.0168 16.9571 12.0168 16.957 12.0168L16.957 12.0168L15.2581 12.1352L16.7494 12.953L16.75 12.9533L16.7502 12.9535L16.7597 12.959C16.7694 12.9647 16.7854 12.9744 16.8068 12.9881C16.8496 13.0156 16.9132 13.0587 16.989 13.1172C17.1417 13.2351 17.3368 13.4103 17.5114 13.6396C17.8516 14.0864 18.1178 14.736 17.8331 15.6301C17.5379 16.5575 16.9562 17.0793 16.2081 17.3351C15.4337 17.5997 14.4497 17.5873 13.386 17.3502L12.9149 17.2451L12.7933 17.7122L12.3158 19.5465L11.8832 19.4308L12.3691 17.6291L12.4989 17.1476L12.0178 17.0165L10.9051 16.7132L10.4205 16.5812L10.2906 17.0663L9.80368 18.8847L9.37819 18.7703L9.86544 16.9625L9.99553 16.4798L9.51289 16.3496L7.24871 15.7389L7.54609 15.0786C7.555 15.081 7.56396 15.0834 7.57298 15.0858C7.80901 15.149 8.09103 15.2234 8.25005 15.2613C8.62847 15.3515 8.92916 15.1787 9.11139 14.9742C9.27946 14.7855 9.37658 14.5465 9.43001 14.3493C9.46647 14.2146 9.70526 13.2523 9.98043 12.1433C10.0402 11.9025 10.1017 11.6548 10.1632 11.4071C10.5113 10.0046 10.8589 8.60883 10.9166 8.40463C10.9854 8.16082 11.0408 7.83655 10.9184 7.52615C10.7806 7.17695 10.4775 6.98074 10.1247 6.88914C9.89892 6.83052 9.63393 6.75631 9.41071 6.69238L9.553 6.17064L11.8301 6.78044L12.3128 6.90972L12.4423 6.42701L12.9287 4.61387L13.3423 4.72608L12.8795 6.44687L12.7496 6.92984L13.2326 7.0596L14.3573 7.36178L14.8411 7.49178L14.9702 7.00768ZM11.9834 12.4545L12.0524 11.9593L11.608 11.8974L11.4985 12.3326L10.7349 15.3686L10.6245 15.8073L11.05 15.9608L11.2198 15.4905C11.05 15.9608 11.0501 15.9609 11.0503 15.9609L11.0506 15.9611L11.0516 15.9614L11.0546 15.9625L11.0646 15.966L11.1003 15.9784C11.1309 15.9889 11.1748 16.0037 11.2303 16.0215C11.341 16.0571 11.4984 16.1053 11.6884 16.1567C12.066 16.2589 12.5831 16.3767 13.1235 16.4311C13.655 16.4845 14.2565 16.4819 14.7784 16.3064C15.3254 16.1224 15.8041 15.737 15.9755 15.0563C16.1471 14.3748 15.9069 13.8098 15.5097 13.3912C15.1314 12.9925 14.6009 12.7099 14.1063 12.5097C13.6033 12.306 13.0907 12.1693 12.7089 12.0836C12.5168 12.0406 12.355 12.0098 12.2403 11.9897C12.1829 11.9797 12.1372 11.9722 12.1052 11.9672L12.0678 11.9615L12.0572 11.96L12.0541 11.9596L12.0531 11.9594L12.0527 11.9594C12.0526 11.9594 12.0524 11.9593 11.9834 12.4545ZM11.8519 10.8845L11.7175 11.3832L12.2203 11.5013C12.306 11.5214 12.3974 11.5437 12.4933 11.5671C13.0697 11.7077 13.8105 11.8884 14.4673 11.8812C14.8603 11.8769 15.2781 11.8072 15.6398 11.5843C16.0169 11.352 16.2827 10.9843 16.4112 10.486C16.563 9.89723 16.3527 9.40309 16.0209 9.0388C15.705 8.69188 15.2641 8.44011 14.8586 8.25962C14.4452 8.0756 14.0247 7.94668 13.7121 7.86416C13.5547 7.8226 13.422 7.79211 13.3278 7.77186C13.2806 7.76172 13.2429 7.75411 13.2165 7.74893L13.1854 7.74297L13.1765 7.74132L13.1738 7.74083L13.1728 7.74066L13.1725 7.7406C13.1724 7.74057 13.1722 7.74055 13.0841 8.23273L13.1722 7.74055L12.7207 7.65972L12.6013 8.10266L11.8519 10.8845Z"
                      stroke="#1570EF"
                    />
                  </svg>
                  <div className="rent-mf__crypto-block">
                    <p className="rent-mf__power-btc">{totalReward.btc}</p>
                    <p className="rent-mf__power-btc">BTC</p>
                  </div>
                  <span className="rent-mf__power-usd">/</span>
                  <div className="rent-mf__crypto-block">
                    <p className="rent-mf__power-usd">{totalReward.usd}</p>
                    <p className="rent-mf__power-usd">USD</p>
                  </div>
                </div>
                <div>
                  <div className="rent-mf__crypto-block">
                    <p className="rent-mf__power-btc-month">
                      + {totalReward.monthlyBtc} BTC
                    </p>
                    <p className="rent-mf__month">{getTranslation('inOneMonth', language)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ASICs Rented */}
            <div className="rent-mf">
              <div className="rent-mf__title-block">
                <p className="rent-mf__title">{getTranslation('asicsRented', language)}</p>
              </div>
              <div className="rent-mf__block">
                <div className="rent-mf__power">
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    alt="logo"
                  >
                    <rect
                      x="0.858496"
                      y="0.525"
                      width="22.95"
                      height="22.95"
                      rx="5.475"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                    />
                    <path
                      d="M15.5418 8.79167L9.12516 8.79167M9.8835 6.75H14.7835C15.7636 6.75 16.2536 6.75 16.628 6.94074C16.9573 7.10852 17.225 7.37623 17.3928 7.70552C17.5835 8.07986 17.5835 8.56991 17.5835 9.55V14.45C17.5835 15.4301 17.5835 15.9201 17.3928 16.2945C17.225 16.6238 16.9573 16.8915 16.628 17.0593C16.2536 17.25 15.7636 17.25 14.7835 17.25H9.8835C8.9034 17.25 8.41336 17.25 8.03901 17.0593C7.70973 16.8915 7.44201 16.6238 7.27423 16.2945C7.0835 15.9201 7.0835 15.4301 7.0835 14.45V9.55C7.0835 8.56991 7.0835 8.07986 7.27423 7.70552C7.44201 7.37623 7.70973 7.10852 8.03901 6.94074C8.41336 6.75 8.9034 6.75 9.8835 6.75Z"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.12939 10.8574L15.5373 10.8575"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                      strokeLinecap="round"
                    />
                    <circle cx="12.3335" cy="14.623" r="0.875" fill="#1570EF" />
                  </svg>
                  <p className="rent-mf__power-value">
                    {asicsRented} <span className="rent-mf__power-title">{getTranslation('asics', language)}</span>
                  </p>
                </div>
                <button className="primary rent-mf__btn" type="button" onClick={handleAddMore}>
                  {getTranslation('addMore', language)}
                </button>
              </div>
            </div>

            {/* Total Mining Power */}
            <div className="rent-mf">
              <div className="rent-mf__title-block">
                <p className="rent-mf__title">{getTranslation('totalMiningPower', language)}</p>
              </div>
              <div className="rent-mf__block">
                <div className="rent-mf__power">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="25"
                    viewBox="0 0 25 25"
                    fill="none"
                    alt="logo"
                  >
                    <rect
                      x="0.775"
                      y="0.762793"
                      width="22.95"
                      height="22.95"
                      rx="5.475"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                    />
                    <path
                      d="M13.1751 6.23779L7.83119 12.6505C7.6219 12.9016 7.51726 13.0272 7.51566 13.1333C7.51427 13.2255 7.55535 13.3132 7.62707 13.3711C7.70956 13.4378 7.87302 13.4378 8.19993 13.4378H12.5751L11.9751 18.2378L17.319 11.8251C17.5283 11.5739 17.633 11.4484 17.6346 11.3423C17.636 11.2501 17.5949 11.1624 17.5232 11.1045C17.4407 11.0378 17.2772 11.0378 16.9503 11.0378H12.5751L13.1751 6.23779Z"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="rent-mf__power-value">
                    {totalMiningPower} <span className="rent-mf__power-title"> TH</span>
                  </p>
                </div>
                <button className="primary rent-mf__btn" type="button" onClick={handleAddMore}>
                  {getTranslation('addMore', language)}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Revenue Section */}
        <div className="rent-mining-result">
          <div className="rent-mining-result__title-block">
            <h2 className="rent-mining-result-block__title">{getTranslation('dailyRevenue', language)}</h2>
          </div>
          <div className="rent-mining-result__block">
            <div className="rent-mining-result__chart">
              <div className="pagination-controls">
                <button
                  className="gray"
                  type="button"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  {getTranslation('previous', language)}
                </button>
                <span>{getTranslation('page', language)} {currentPage}</span>
                <button
                  className="gray"
                  type="button"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  {getTranslation('next', language)}
                </button>
              </div>
              <div className="chart">
                <HighchartsReact
                  highcharts={Highcharts}
                  options={chartOptions}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Rented ASICs Table */}
        <div className="rented-asics">
          <h2 className="rented-asics__title">{getTranslation('rentedAsics', language)}</h2>
          <div className="rented-asics__block">
            <table className="rented-asics_tab">
              <thead className="rented-asics__header">
                <tr className="rented-asics__header-title-block">
                  <th className="rented-asics__header-title">{getTranslation('deviceId', language)}</th>
                  <th className="rented-asics__header-title">{getTranslation('rentedAsicModelTH', language)}</th>
                  <th className="rented-asics__header-title">{getTranslation('daysLeft', language)}</th>
                  <th className="rented-asics__header-title">{getTranslation('yesterdayProfit', language)}</th>
                  <th className="rented-asics__header-title">{getTranslation('totalMinedBtc', language)}</th>
                  <th className="rented-asics__header-title">{getTranslation('hostingFeePaymentSchedule', language)}</th>
                  <th className="rented-asics__header-title">{getTranslation('hostingFeeAmount', language)}</th>
                  <th className="rented-asics__header-title">{getTranslation('hostingFeeStatus', language)}</th>
                </tr>
              </thead>
              <tbody className="rented-asics__item">
                {rentedAsics.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#667085' }}>
                      {getTranslation('noRentedAsics', language)}
                    </td>
                  </tr>
                ) : (
                  rentedAsics.map((asic) => (
                    <tr key={asic.deviceId} className="rented-asics__item-block">
                      <td className="first">
                        <div className="wrapper_td">
                          <p className="wrapper_id">{asic.deviceId}</p>
                        </div>
                      </td>
                      <td><p>{asic.model}</p></td>
                      <td><p>{asic.daysLeft}</p></td>
                      <td><p>{asic.yesterdayProfit}</p></td>
                      <td><p>{asic.totalMinedBtc}</p></td>
                      <td><p>{asic.hostingFeeSchedule}</p></td>
                      <td><p>{asic.hostingFeeAmount}</p></td>
                      <td><p className="rented-asics__item-status">{asic.hostingFeeStatus}</p></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* My Account Section */}
      <div className="rent-account__block">
        <h2 className="rent-account__title">My Account</h2>
        <div className="rent-account__block-group">
          <div className="rent-account-block">
            <div className="rent-account-button-block">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <g clipPath="url(#clip0_3963_124262)">
                  <path d="M32 16C32 19.1645 31.0616 22.2579 29.3035 24.8891C27.5454 27.5203 25.0466 29.5711 22.1229 30.7821C19.1993 31.9931 15.9823 32.3099 12.8786 31.6926C9.77487 31.0752 6.92394 29.5514 4.6863 27.3137C2.44866 25.0761 0.924806 22.2251 0.307443 19.1214C-0.309921 16.0177 0.00693248 12.8007 1.21793 9.87707C2.42894 6.95345 4.4797 4.45459 7.11088 2.69649C9.74207 0.938384 12.8355 0 16 0C20.2435 0 24.3131 1.68571 27.3137 4.68629C30.3143 7.68687 32 11.7565 32 16Z" fill="#F7931A"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M11.372 7.30706L15.049 8.29179L15.8709 5.22852L17.7094 5.72742L16.9196 8.66415L18.4192 9.06706L19.2105 6.09833L21.081 6.5987L20.2752 9.58342C20.2752 9.58342 23.3298 10.2598 24.0483 12.7442C24.7669 15.2285 22.4687 16.5332 21.7589 16.5827C21.7589 16.5827 24.4352 18.0503 23.516 20.9376C22.5967 23.8249 19.7749 24.3412 16.8061 23.6794L16.0003 26.7747L14.1298 26.2743L14.9516 23.2271L13.468 22.8227L12.6461 25.8918L10.7901 25.3929L11.6134 22.3383L7.83887 21.3202L8.79014 19.2082C8.79014 19.2082 9.85487 19.4991 10.2578 19.5951C10.6607 19.6911 10.9196 19.2722 11.033 18.8532C11.1465 18.4343 12.8556 11.4918 13.017 10.9202C13.1785 10.3485 13.113 9.90197 12.4352 9.72597C11.7574 9.54997 10.8352 9.27506 10.8352 9.27506L11.372 7.30706ZM15.081 16.5012L14.0629 20.5492C14.0629 20.5492 19.1116 22.3718 19.7574 19.8074C20.4032 17.2431 15.081 16.5012 15.081 16.5012ZM15.5494 14.5812L16.5487 10.8722C16.5487 10.8722 20.8716 11.646 20.3392 13.71C19.8069 15.774 17.2585 14.9827 15.5494 14.5812Z" fill="white"/>
                </g>
                <defs>
                  <clipPath id="clip0_3963_124262">
                    <rect width="32" height="32" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
              <p className="rent-account-button">{(user.balance / 130000).toFixed(8)} BTC</p>
            </div>
            <button className="rent-account-button-plus" onClick={() => window.location.href = '/deposit'}>
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g filter="url(#filter0_d_3963_124299)">
                  <rect x="2" y="1" width="40" height="40" rx="8" fill="#F79009"/>
                  <path d="M22 28.5L22 13.5M14.5 21H29.5" stroke="white" strokeWidth="1.67"/>
                </g>
                <defs>
                  <filter id="filter0_d_3963_124299" x="0" y="0" width="44" height="44" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                    <feOffset dy="1"/>
                    <feGaussianBlur stdDeviation="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 0.0627451 0 0 0 0 0.0941176 0 0 0 0 0.156863 0 0 0 0.05 0"/>
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3963_124299"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3963_124299" result="shape"/>
                  </filter>
                </defs>
              </svg>
            </button>
          </div>

          <div className="rent-account-block">
            <div className="rent-account-button-block">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M16 32C24.8366 32 32 24.8366 32 16C32 7.16344 24.8366 0 16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32Z" fill="#3CCB7F"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M22.5 19.154C22.5 21.724 20.414 23.43 17.334 23.687V26H15.224V23.664C13.1407 23.4291 11.1613 22.6289 9.5 21.35L11.052 19.224C12.435 20.299 13.744 21 15.321 21.234V16.654C11.78 15.766 10.131 14.481 10.131 11.841C10.131 9.318 12.192 7.589 15.224 7.355V6H17.334V7.402C18.9831 7.58518 20.5554 8.19754 21.894 9.178L20.535 11.374C19.468 10.603 18.377 10.112 17.237 9.879V14.318C20.924 15.206 22.5 16.631 22.5 19.154ZM15.32 13.827V9.715C13.793 9.832 12.993 10.65 12.993 11.678C12.993 12.658 13.453 13.29 15.321 13.828L15.32 13.827ZM19.638 19.317C19.638 18.267 19.128 17.636 17.237 17.098V21.328C18.765 21.21 19.638 20.439 19.638 19.318V19.317Z" fill="white"/>
              </svg>
              <p className="rent-account-button">{user.balance.toFixed(2)} $</p>
            </div>
            <button className="rent-account-button-plus" onClick={() => window.location.href = '/deposit'}>
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g filter="url(#filter0_d_3963_124300)">
                  <rect x="2" y="1" width="40" height="40" rx="8" fill="#F79009"/>
                  <path d="M22 28.5L22 13.5M14.5 21H29.5" stroke="white" strokeWidth="1.67"/>
                </g>
                <defs>
                  <filter id="filter0_d_3963_124300" x="0" y="0" width="44" height="44" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                    <feOffset dy="1"/>
                    <feGaussianBlur stdDeviation="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 0.0627451 0 0 0 0 0.0941176 0 0 0 0 0.156863 0 0 0 0.05 0"/>
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3963_124300"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3963_124300" result="shape"/>
                  </filter>
                </defs>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentedAsics;

